import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePdfGeneration } from '@/hooks/pdf/usePdfGeneration'
import { usePdfHandlers } from '@/hooks/pdf/usePdfHandlers'
import { usePdfSetters } from '@/hooks/pdf/usePdfSetters'
import { usePdfState } from '@/hooks/pdf/usePdfState'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import type { StatusKey, PdfStatusKey } from '@messenger/types'
import { M3 } from '@messenger/consts'

export type GenerationState = {
  status?: PdfStatusKey
  progress?: number
  pdf?: string
}

export type ProofGalleryDetails = {
  pdfId: string
  galleryToken: string | null
  screenshotUrls: string[]
}

/**
 * Complete PDF state structure for a single product
 * Used to persist PDF generation state across tab switches
 */
export type PdfState = {
  // Generation control state
  generating: boolean
  generatingText: string
  pendingCloseOperation: boolean

  // PDF generation progress and result
  generationState: GenerationState

  // Modal and UI visibility state
  showPdfViewer: boolean
  disclaimerAcknowledged: boolean

  // Button state and permissions
  previewCustomizeProductHeaderDropdownsDisabled: boolean
  canCustomize: boolean
  canRevertFinalization: boolean
  isProof: boolean

  // Deferred product status update flags - used to prevent premature component re-renders
  shouldSetProductStatusToFinalized: boolean
  shouldSetProductStatusToEditing: boolean
  shouldSetProductStatusToProofSent: boolean

  // Proof gallery metadata for quick navigation to screenshot gallery
  proofGalleryDetails: ProofGalleryDetails | null
}

export type UpdatePdfStateFunction = (updates: Partial<PdfState>) => void

type UsePdfViewerParams = {
  getOrderProducts: () => void
  order: {
    id: number
    case_id?: string
    funeral_home_id: number
  }
  pageSet: {
    id: number
  }
  params: URLSearchParams
  productInfo: {
    id: number
    status?: string
  }
  productStatus: StatusKey
  setProductStatus: (status: StatusKey) => Promise<void>
  selectedLayout: {
    paper_height: number
    paper_width: number
  }
  pdfState: PdfState
  updatePdfState: UpdatePdfStateFunction
  applicationType: string
}

export const usePdfViewer = ({
  getOrderProducts,
  order,
  pageSet,
  params,
  productInfo,
  productStatus,
  setProductStatus,
  selectedLayout,
  pdfState,
  updatePdfState,
  applicationType,
}: UsePdfViewerParams) => {
  const { t } = useTranslation()

  const setters = usePdfSetters({ updatePdfState })
  const state = usePdfState({ pdfState })

  const cleanupPdfGeneration = useCallback(() => {
    updatePdfState({
      previewCustomizeProductHeaderDropdownsDisabled: false,
      generating: false,
      isProof: false,
      canRevertFinalization: false,
      showPdfViewer: false,
      generatingText:
        applicationType === M3
          ? t('pdf.generateProofM3')
          : t('pdf.generateProof'),
    })
  }, [updatePdfState, t, applicationType])

  const { generatePDF: generatePDFFromHook } = usePdfGeneration({
    order,
    pageSet,
    productInfo,
    setProductStatus,
    updatePdfState,
    cleanupPdfGeneration,
    applicationType,
  })

  const generatePDF = useCallback(
    (isProof: boolean) => {
      return generatePDFFromHook(isProof, selectedLayout)
    },
    [generatePDFFromHook, selectedLayout],
  )

  const isAdmin = useIsAdmin()

  const handlers = usePdfHandlers({
    isUserAllowedToSeeFinalPdf: isAdmin,
    pageSet,
    params,
    productStatus,
    setProductStatus,
    selectedLayout,
    updatePdfState,
    generatePDF,
    getOrderProducts,
    cleanupPdfGeneration,
    generating: pdfState.generating,
    generatingText: pdfState.generatingText,
    shouldSetProductStatusToEditing: pdfState.shouldSetProductStatusToEditing,
    shouldSetProductStatusToProofSent:
      pdfState.shouldSetProductStatusToProofSent,
    shouldSetProductStatusToFinalized:
      pdfState.shouldSetProductStatusToFinalized,
    applicationType,
  })

  useEffect(() => {
    const generationFinishedAndCloseOperationReady =
      !state.generating && state.pendingCloseOperation
    if (generationFinishedAndCloseOperationReady) {
      setters.setPendingCloseOperation(false)
      handlers.handleDeferredClose()
    }
  }, [state.generating, state.pendingCloseOperation, handlers, setters])

  return {
    generatePDF,
    handlers,
    setters,
    state,
  }
}
